import { useMap } from "@vis.gl/react-maplibre";
import type { MapGeoJSONFeature, MapLayerMouseEvent } from "maplibre-gl";
import { useEffect } from "react";

import { getAliveMap } from "./maplibre-map.utils";

/**
 * Layer-scoped pointer wiring for a MapLibre layer: subscribes, keeps the canvas cursor in step, and — the
 * part that is easy to forget in a hand-rolled copy — unsubscribes and resets the cursor on unmount, so a
 * layer removed mid-hover cannot leave a pointer cursor stuck on the map.
 *
 * `enabled` exists because a delegated listener is not free: MapLibre implements `map.on(type, layerId, fn)`
 * as a `mousemove` delegate that runs `queryRenderedFeatures` for that layer on every pointer move, so a
 * layer that is not currently drawn should not be subscribed at all.
 */
export const useMapLibreLayerPointer = ({
  layerId,
  enabled = true,
  onHover,
  onLeave,
  onClick,
}: {
  layerId: string;
  enabled?: boolean;
  /**
   * Fired on pointer move over the layer, with the topmost feature under the pointer. The event comes
   * along for tooltips that track the cursor rather than the feature (Leaflet's `sticky: true`).
   */
  onHover?: (feature: MapGeoJSONFeature, event: MapLayerMouseEvent) => void;
  onLeave?: () => void;
  onClick?: (feature: MapGeoJSONFeature) => void;
}): void => {
  const { current: mapRef } = useMap();

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !enabled) {
      return;
    }

    const withFeature =
      (
        handler: (
          feature: MapGeoJSONFeature,
          event: MapLayerMouseEvent,
        ) => void,
      ) =>
      (event: MapLayerMouseEvent): void => {
        const feature = event.features?.[0];

        if (feature) {
          handler(feature, event);
        }
      };

    const showPointer = (): void => {
      map.getCanvas().style.cursor = "pointer";
    };
    const clearPointer = (): void => {
      map.getCanvas().style.cursor = "";
      onLeave?.();
    };

    const handleHover = onHover
      ? withFeature((feature, event) => {
          showPointer();
          onHover(feature, event);
        })
      : showPointer;
    const handleClick = onClick ? withFeature(onClick) : undefined;

    map.on("mousemove", layerId, handleHover);
    map.on("mouseenter", layerId, showPointer);
    map.on("mouseleave", layerId, clearPointer);
    // Pointer left the canvas entirely (e.g. moved onto a popup) — clear any stuck hover state.
    map.on("mouseout", clearPointer);

    if (handleClick) {
      map.on("click", layerId, handleClick);
    }

    return () => {
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      aliveMap.off("mousemove", layerId, handleHover);
      aliveMap.off("mouseenter", layerId, showPointer);
      aliveMap.off("mouseleave", layerId, clearPointer);
      aliveMap.off("mouseout", clearPointer);

      if (handleClick) {
        aliveMap.off("click", layerId, handleClick);
      }

      aliveMap.getCanvas().style.cursor = "";
    };
  }, [mapRef, layerId, enabled, onHover, onLeave, onClick]);
};
